import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import defer from 'lodash/defer';
import { selectExperimentsReady } from '../../../../../ui-kitchen/src/components/Experiments/useExperiment';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { setLocationPath } from '../../../../../ui-kitchen/src/utils/locationUtils';
import { fetchS3JsonFile, isSectionHidden, scrollTo } from '../AmexMarketingUtils/AmexUtils';
import AmexHeader from '../AmexMarketingUtils/AmexHeader';
import AmexLoading from '../AmexMarketingUtils/AmexLoading';
import ResyRewardsBanner from './ResyRewardsBanner';
import ResyRewardsIntro from './ResyRewardsIntro';
import ResyRewardsAppHome from './ResyRewardsAppHome';
import ResyRewardsHowItWorks from './ResyRewardsHowItWorks';
import ResyRewardsBonus from './ResyRewardsBonus';
import ResyRewardsFAQs from './ResyRewardsFAQs';
import ResyRewardsTerms from './ResyRewardsTerms';
import { FILE_NAME, FOLDER_NAME } from './constants';
import selectIsResyRewardsEnabled from './selectors';
import './ResyRewards.scss';

const ResyRewards = ({
  $location,
  $rootScope,
  $routeParams,
  Analytics,
  areExperimentsReady,
  isAuthenticated,
  isNonUS,
  isResyRewardsEnabled,
  ResyAmexConfig: {
    baseRequestUrl,
  },
}) => {
  const [contentData, setContentData] = useState({});
  const { matchMedia } = $rootScope;
  const isLoaded = Object.keys(contentData).length > 0;
  const isPageEnabled = contentData.is_page_enabled ?? false;
  const isFlagOff = areExperimentsReady && !isResyRewardsEnabled;
  const shouldRedirect = isFlagOff || (isLoaded && !isPageEnabled);

  useEffect(() => {
    if (!isResyRewardsEnabled) return undefined;
    let isCancelled = false;

    const fetchDataFile = async () => {
      const data = await fetchS3JsonFile(baseRequestUrl, FOLDER_NAME, FILE_NAME);
      if (!data || isCancelled) return;
      setContentData(data);
    };
    fetchDataFile();

    return () => {
      isCancelled = true;
    };
  }, [baseRequestUrl, isResyRewardsEnabled]);

  useEffect(() => {
    if (!shouldRedirect) return;
    setLocationPath('/404', () => defer(() => $rootScope.$apply()));
    defer(() => $rootScope.$digest());
  }, [shouldRedirect]);

  useEffect(() => {
    if (!isPageEnabled) return undefined;

    const scrollToHash = () => {
      if (window.location.hash) scrollTo(window.location.hash);
    };
    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);

    return () => window.removeEventListener('hashchange', scrollToHash);
  }, [isPageEnabled]);

  if (!areExperimentsReady) return <AmexLoading />;
  if (!isResyRewardsEnabled) return null;
  if (!isLoaded) return <AmexLoading />;
  if (!isPageEnabled) return null;

  const {
    asset_url: assetsUrl = '',
    aws_folder_name: awsFolderName,
    app_store_url: appStoreUrl,
    resy_header: resyHeaderConfig,
    hero: heroConfig,
    intro: introConfig,
    app_home: appHomeConfig,
    how_it_works: howItWorksConfig,
    bonus: bonusConfig,
    faqs: faqsConfig,
    closing: closingConfig,
    terms: termsConfig,
  } = contentData;

  const urlPrefix = `${assetsUrl}${awsFolderName}`;
  const sharedProps = {
    eventAnalytics: Analytics.event,
    matchMedia,
    urlPrefix,
  };

  return (
    <section className="ResyRewards">
      <AmexHeader
        Analytics={Analytics}
        isAuthenticated={isAuthenticated}
        isNonUSLocation={isNonUS}
        $rootScope={$rootScope}
        $routeParams={$routeParams}
        $location={$location}
        matchMedia={matchMedia}
        resyHeaderConfig={resyHeaderConfig}
      />
      <div className="ResyRewards__content">
        {!isSectionHidden(contentData, 'hero') && (
          <ResyRewardsBanner
            {...sharedProps}
            appStoreUrl={appStoreUrl}
            bannerConfig={heroConfig}
            videoUrlPrefix={urlPrefix}
          />
        )}
        {!isSectionHidden(contentData, 'intro') && (
          <ResyRewardsIntro
            introConfig={introConfig}
            matchMedia={matchMedia}
            urlPrefix={urlPrefix}
          />
        )}
        {!isSectionHidden(contentData, 'app_home') && (
          <ResyRewardsAppHome
            appHomeConfig={appHomeConfig}
            matchMedia={matchMedia}
            urlPrefix={urlPrefix}
          />
        )}
        {!isSectionHidden(contentData, 'how_it_works') && (
          <ResyRewardsHowItWorks
            {...sharedProps}
            appStoreUrl={appStoreUrl}
            howItWorksConfig={howItWorksConfig}
          />
        )}
        {!isSectionHidden(contentData, 'bonus') && (
          <ResyRewardsBonus
            {...sharedProps}
            bonusConfig={bonusConfig}
            isClosing
          />
        )}
        {!isSectionHidden(contentData, 'faqs') && (
          <ResyRewardsFAQs
            eventAnalytics={Analytics.event}
            faqsConfig={faqsConfig}
          />
        )}
        {!isSectionHidden(contentData, 'closing') && (
          <ResyRewardsBanner
            {...sharedProps}
            appStoreUrl={appStoreUrl}
            bannerConfig={closingConfig}
            isClosing
          />
        )}
        {!isSectionHidden(contentData, 'terms') && (
          <ResyRewardsTerms termsConfig={termsConfig} />
        )}
      </div>
    </section>
  );
};

ResyRewards.propTypes = {
  $location: PropTypes.shape({
    path: PropTypes.func.isRequired,
    search: PropTypes.func.isRequired,
  }).isRequired,
  $rootScope: PropTypes.shape({
    $apply: PropTypes.func,
    $digest: PropTypes.func.isRequired,
    matchMedia: PropTypes.shape({
      xs: PropTypes.bool,
      sm: PropTypes.bool,
      md: PropTypes.bool,
      lg: PropTypes.bool,
      ipadLandscape: PropTypes.bool,
      xl: PropTypes.bool,
    }).isRequired,
    metadata: PropTypes.shape({
      image_twitter: PropTypes.string,
      image_facebook: PropTypes.string,
    }),
  }).isRequired,
  $routeParams: PropTypes.shape({}).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  areExperimentsReady: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isNonUS: PropTypes.bool,
  isResyRewardsEnabled: PropTypes.bool.isRequired,
  ResyAmexConfig: PropTypes.shape({
    baseRequestUrl: PropTypes.string.isRequired,
  }).isRequired,
};

ResyRewards.defaultProps = {
  isNonUS: false,
};

const mapStateToProps = (state) => ({
  areExperimentsReady: selectExperimentsReady(state),
  isAuthenticated: selectIsAuthenticated(state),
  isNonUS: state.ipdata?.isNonUS,
  isResyRewardsEnabled: selectIsResyRewardsEnabled(state),
});

ResyRewards.displayName = 'ResyRewards';
export { ResyRewards as ComponentResyRewardsUnderTest, mapStateToProps };
export default connect(mapStateToProps)(ResyRewards);
